import React, { useMemo } from "react";

import pdf from "@react-pdf/renderer";
import { sum } from "lodash";

import { graphql } from "@/gql";
import { colors } from "@/theme/overrides/colors";
import { FragmentType } from "@/utils/type";

import ContentHeader from "./components/ContentHeader";
import DayIndicator from "./components/DayIndicator";
import Footer from "./components/Footer";
import Section from "./components/Section";
import Subheader from "./components/Subheader";
import {
  mulish,
  mulishBold,
  mulishExtraBold,
  mulishExtraLight,
  mulishLight,
  mulishMedium,
  mulishSemiBold,
} from "./fonts";

const { Page, StyleSheet, Font, View } = pdf;

Font.register({
  family: "Mulish",
  fonts: [
    {
      src: mulish,
      fontWeight: "normal",
    },
    {
      src: mulishBold,
      fontWeight: "bold",
    },
    {
      src: mulishExtraBold,
      fontWeight: "ultrabold",
    },
    {
      src: mulishExtraLight,
      fontWeight: "ultralight",
    },
    {
      src: mulishLight,
      fontWeight: "light",
    },
    {
      src: mulishMedium,
      fontWeight: "medium",
    },
    {
      src: mulishSemiBold,
      fontWeight: "semibold",
    },
  ],
});

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: "0px",
    fontFamily: "Mulish",
    color: colors.dark,
    fontSize: "16px",
  },
  container: {
    padding: "12px 24px 0px 24px",
  },
});

type Props = {
  headerData: FragmentType<typeof pdfMealTemplateHeaderFragment>;
  plannedMealsData: FragmentType<typeof pdfMealTemplatePlannedMealsFragment>;
  day: number;
};

const pdfMealTemplateHeaderFragment = graphql(`
  fragment PDFMealTemplate_Header on Program {
    ...PDFMealPlanSubheader
    name
    startDate
    endDate
    mealPlan {
      id
      recommendedCalories
      ...PDFMealPlanContentHeader
      ...PDFMealPlanSection_Macronutrients
    }
  }
`);

const pdfMealTemplatePlannedMealsFragment = graphql(`
  fragment PDFMealTemplate_PlannedMeals on PlannedMealsByDate {
    date
    plannedMeals {
      ...PDFMealPlanSection
    }
  }
`);

const PDFMealTemplate: React.FC<Props> = ({
  headerData,
  plannedMealsData,
  day,
}) => {
  const totalCalories = useMemo(() => {
    return sum(
      plannedMealsData.plannedMeals.map(
        (pm) => pm.itemsByDate[day - 1].totalEnergyKcal
      )
    );
  }, [plannedMealsData, day]);
  return (
    <Page size="A4" style={styles.page}>
      <Subheader data={headerData} title={headerData.name} />
      <View style={styles.container}>
        <DayIndicator day={`Day ${day}`} />
        <ContentHeader
          data={headerData.mealPlan}
          totalCalories={totalCalories}
        />
        {plannedMealsData.plannedMeals.map((data, index) => (
          <Section
            key={index}
            data={data}
            macronutrients={headerData.mealPlan}
            day={day}
          />
        ))}
        <Footer />
      </View>
    </Page>
  );
};

export default PDFMealTemplate;
